import { CurrencyPipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, inject, linkedSignal, Pipe, PipeTransform, signal } from "@angular/core";
import { AsinSeed, AsinType, CostOfGoods, CustomField, Marketplace } from "@front/m19-api-client";
import { AsinService } from "@front/m19-services";
import {
  IButtonDirective,
  ITooltipDirective,
  ISelectMenuImports,
  MODAL_DATA,
  ModalRef,
  IIconComponent,
  SpinnerComponent,
} from "@front/m19-ui";
import { DateUtils, Utils } from "@front/m19-utils";
import { TranslocoDirective } from "@jsverse/transloco";
import { of } from "rxjs";
import { AjaxError } from "rxjs/ajax";
import { catchError, map } from "rxjs/operators";
import { CatalogProductEx } from "@m19-board/services/managed-catalog-page.service";
import { CsvSeedType, CsvMatchType } from "../catalog-sections/catalog.component";
import { SeedCsvRow } from "../catalog-sections/seed-csv-row";

enum UploadMode {
  UPLOAD_COGS = "UPLOAD_COGS",
  UPLOAD_SEEDS = "UPLOAD_SEEDS",
  UPLOAD_CF = "UPLOAD_CF",
}

const UploadModeLabels: { [key in UploadMode]: string } = {
  UPLOAD_COGS: "Upload Cost Of Goods",
  UPLOAD_SEEDS: "Upload Seed/Blacklist",
  UPLOAD_CF: "Upload Product Tags",
};

@Pipe({ name: "uploadModeLabel", standalone: true })
export class UploadModeLabelPipe implements PipeTransform {
  transform(mode: UploadMode | undefined | null): string {
    return mode ? UploadModeLabels[mode] : "";
  }
}

export type UploadResult<T> = {
  uploaded: T[];
  errors: string[];
  warnings: string[];
};

@Component({
  selector: "app-catalog-data-modal",
  templateUrl: "./upload-catalog-data-modal.component.html",
  styleUrls: ["./upload-catalog-data-modal.component.scss"],
  imports: [
    TranslocoDirective,
    SpinnerComponent,
    ISelectMenuImports,
    CurrencyPipe,
    IButtonDirective,
    ITooltipDirective,
    IIconComponent,
    UploadModeLabelPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadCatalogDataModalComponent {
  private asinService = inject(AsinService);

  modalData = inject(MODAL_DATA) as UploadCatalogDataModalData;
  modalRef = inject(ModalRef);

  fileUploadError = "";
  readonly loading = signal(false);

  protected readonly uploadModes = [UploadMode.UPLOAD_SEEDS, UploadMode.UPLOAD_COGS, UploadMode.UPLOAD_CF] as const;

  selectedUploadMode = linkedSignal<UploadMode>(() => this.uploadModes[0]);
  readonly UploadMode = UploadMode;
  readonly textareaContent = linkedSignal({ source: this.selectedUploadMode, computation: () => "" });

  isUploadButtonDisabled(content: string): boolean {
    return !content || content.trim().length === 0;
  }

  addCogs(cogs: string): void {
    const cogsToAdd: CostOfGoods[] = [];
    const errors: string[] = [];
    const warnings: string[] = [];
    const rows = cogs.split(/[\n]+/);
    let lineIndex = 0;
    for (const row of rows) {
      const trimmedRow = row.trim();
      if (trimmedRow === "") {
        continue;
      }
      lineIndex++;
      const sp = trimmedRow.split(/[,; \t]+/);
      if (sp.length < 2) {
        errors.push(`Line ${lineIndex} "${trimmedRow}" is not properly formatted`);
        continue;
      }
      const asin = Utils.normalizeASIN(sp[0]);
      const cog = Number.parseFloat(sp[1]);
      if (asin && Utils.isValidAsin(asin) && !isNaN(cog)) {
        if (!this.modalData.catalogAsins.find((a) => a.asin == asin)) {
          errors.push(`Catalog does not contain ASIN ${asin} on ${lineIndex} ("${trimmedRow}")`);
          continue;
        }
        cogsToAdd.push({
          accountId: this.modalData.accountId,
          marketplace: this.modalData.marketplace,
          asin: asin,
          costOfGoods: cog,
        });
      } else {
        errors.push(`Line ${lineIndex} "${trimmedRow}" is not properly formatted`);
      }
    }
    if (cogsToAdd.length == 0) {
      errors.push("No Cost Of Goods to import");
      this.modalData.onCogsUpload({ uploaded: cogsToAdd, errors: errors, warnings: warnings });
      this.modalRef.close();
      return;
    }
    const addedCogs: CostOfGoods[] = [];
    this.asinService.updateCostOfGood(this.modalData.accountId, this.modalData.marketplace, cogsToAdd).subscribe(() => {
      for (const cogToAdd of cogsToAdd) {
        addedCogs.push(cogToAdd);
      }
      this.modalData.onCogsUpload({ uploaded: addedCogs, errors: errors, warnings: warnings });
    });
    this.modalRef.close();
  }

  addCFs(cf: string): void {
    const cfToAdd: CustomField[] = [];
    const errors: string[] = [];
    const warnings: string[] = [];
    const rows = cf.split(/[\n]+/);
    let lineIndex = 0;
    for (const row of rows) {
      const trimmedRow = row.trim();
      if (trimmedRow === "") {
        continue;
      }
      lineIndex++;
      const sp = trimmedRow.split(/[,;\t]/);
      if (sp.length < 2 || sp.length > 3) {
        errors.push(`Line ${lineIndex} "${trimmedRow}" is not properly formatted`);
        continue;
      }
      const asin = Utils.normalizeASIN(sp[0]);
      const field1 = sp[1]?.trim();
      const field2 = sp[2]?.trim();
      if (asin && Utils.isValidAsin(asin)) {
        if (!this.modalData.catalogAsins.find((a) => a.asin == asin)) {
          errors.push(`Catalog does not contain ASIN ${asin} on ${lineIndex} ("${trimmedRow}")`);
          continue;
        }
        cfToAdd.push({
          asin: asin,
          field1: field1,
          field2: field2,
        });
      } else {
        errors.push(`Line ${lineIndex} "${trimmedRow}" is not properly formatted`);
      }
    }
    if (cfToAdd.length == 0) {
      errors.push("No Custom Field to import");
      this.modalData.onCFsUpload({ uploaded: cfToAdd, errors: errors, warnings: warnings });
      this.modalRef.close();
      return;
    }
    this.asinService.updateCustomFields(this.modalData.accountId, this.modalData.marketplace, cfToAdd).subscribe({
      next: () => {
        this.modalData.onCFsUpload({ uploaded: cfToAdd, errors: errors, warnings: warnings });
      },
      error: (err) => {
        errors.push("Failed to import every CustomFields");
        this.modalData.onCFsUpload({ uploaded: cfToAdd, errors: errors, warnings: warnings });
      },
    });

    this.modalRef.close();
  }

  addSeeds(seeds: string): void {
    const kwTargetings: Map<string, AsinSeed[]> = new Map();
    const productTargetings: Map<string, AsinSeed[]> = new Map();
    const asins: Set<string> = new Set();
    const errors: string[] = [];
    const warnings: string[] = [];
    const rows = seeds.split(/[\n]+/);
    let lineIndex = 0;
    for (const row of rows) {
      const trimmedRow = row.trim();
      if (trimmedRow === "" || trimmedRow === "ASIN,Type,MatchType,Value") {
        // discard empty or header rows
        continue;
      }
      lineIndex++;
      const sp = trimmedRow.split(/[,;\t]/);
      if (sp.length < 4) {
        errors.push(`Line ${lineIndex} "${trimmedRow}" is not properly formatted`);
        continue;
      }
      const asin = Utils.normalizeASIN(sp[0]);
      if (!Utils.isValidAsin(asin)) {
        errors.push(`Invalid ASIN ${asin} on line ${lineIndex} ("${trimmedRow}")`);
        continue;
      }
      const catalogAsin = this.modalData.catalogAsins.find((a) => a.asin == asin);
      if (!catalogAsin) {
        errors.push(`Catalog does not contain ASIN ${asin} on ${lineIndex} ("${trimmedRow}")`);
        continue;
      }
      if (catalogAsin.asinType == AsinType.PARENT) {
        errors.push(`Cannot set seed on parent ASIN ${asin} on line ${lineIndex} ("${trimmedRow}")`);
        continue;
      }
      const type: CsvSeedType = CsvSeedType[sp[1] as CsvSeedType];
      if (!type) {
        errors.push(`Invalid SEED/BLACKLIST type on line ${lineIndex} ("${trimmedRow}")`);
        continue;
      }
      const matchType = sp[2] as CsvMatchType;
      if (!matchType) {
        errors.push(`Invalid match type on line ${lineIndex} ("${trimmedRow}")`);
        continue;
      }
      if (matchType == CsvMatchType.phrase && type == CsvSeedType.SEED) {
        errors.push(
          `Phrase match type is not supported for SEED keywords (only exact is supported) on line ${lineIndex} ("${trimmedRow}")`,
        );
        continue;
      }
      for (let i = 3; i < sp.length; i++) {
        const value = sp[i];
        if (!value) {
          continue;
        }
        const item: AsinSeed = new SeedCsvRow(asin, type, matchType, value).toAsinSeed(
          this.modalData.accountId,
          this.modalData.marketplace,
        );
        if (matchType == CsvMatchType.targetedProduct) {
          const items = productTargetings.get(asin) ?? [];
          productTargetings.set(asin, [...items, item]);
        } else {
          const items = kwTargetings.get(asin) ?? [];
          kwTargetings.set(asin, [...items, item]);
        }
      }
      asins.add(asin);
    }
    if (asins.size == 0) {
      errors.push("No seed/blacklist to import");
      this.modalData.onSeedsUpload({ uploaded: [], errors: errors, warnings: warnings });
      this.modalRef.close();
      return;
    }
    this.asinService
      .setBulkAsinSeeds(
        this.modalData.accountId,
        this.modalData.marketplace,
        Array.from(asins).map((asin) => {
          return { asin, keywords: kwTargetings.get(asin) ?? [], products: productTargetings.get(asin) ?? [] };
        }),
      )
      .pipe(
        map((result) => {
          // warnings or errors might be returned
          errors.push(...result.errors);
          result.warnings.forEach((e) => warnings.push(`Some seed/blacklist values have been discarded - ${e}`));
          return result.asins;
        }),
        catchError((err) => {
          const ajaxError = err as AjaxError;
          errors.push(`Impossible to update seed/blacklist: ${ajaxError.response?.message ?? ajaxError.message}`);
          return of([]);
        }),
      )
      .subscribe((updatedAsins) => {
        this.loading.set(false);
        this.modalData.onSeedsUpload({ uploaded: updatedAsins, errors: errors, warnings: warnings });
        this.modalRef.close();
      });
    this.loading.set(true);
  }

  onPaste(event: ClipboardEvent): void {
    // when pasting data from Excel, trim and replace tabs by commas
    const pastedData = event.clipboardData?.getData("text") ?? "";
    const processedData = pastedData.trim().replaceAll(/\t+/g, ",");
    // this will replace text area selection by the new value
    const textarea = <HTMLInputElement>event.target;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    textarea.value = textarea.value.substring(0, start ?? 0) + processedData + textarea.value.substring(end ?? 0);
    this.textareaContent.set(textarea.value);
    event.preventDefault();
  }

  onCsvUpload(files: FileList | null): void {
    if (files && files.length > 0) {
      const csvFile = files[0];
      if (csvFile.type !== "text/csv" && csvFile.type !== "application/vnd.ms-excel") {
        this.fileUploadError = `${csvFile.name} is not a CSV file`;
        return;
      }
      const reader: FileReader = new FileReader();
      reader.readAsText(csvFile);
      reader.onload = () => {
        const csv: string = reader.result as string;
        if (this.selectedUploadMode() === UploadMode.UPLOAD_SEEDS) {
          this.addSeeds(csv);
        } else if (this.selectedUploadMode() === UploadMode.UPLOAD_COGS) {
          this.addCogs(csv);
        } else {
          this.addCFs(csv);
        }
      };
    }
  }
}

export interface UploadCatalogDataModalData {
  currencySymbol: string;
  accountId: string;
  marketplace: Marketplace;
  catalogAsins: CatalogProductEx[];
  onCogsUpload: (result: UploadResult<CostOfGoods>) => void;
  onSeedsUpload: (result: UploadResult<string>) => void;
  onCFsUpload: (result: UploadResult<CustomField>) => void;
}
